// @flow

// PinType is defined as any from app/common/react/components/growth/unauth/lib/PropTypes
// eslint-disable-next-line pinterest/only-safe-imports
import { smartTruncate, removeMultipleSpaces } from 'app/common/lib/react/strUtils';

type PinType = any;
const descriptionMaxLength = 496;

// PinIconAndAttribution Utils
const detectAvailable = (pin: PinType) => {
  const { place_summary: placeSummary, rich_summary: richSummary } = pin;
  const hasRichSummary = richSummary && richSummary.type_name !== 'mobile application';
  const hasRichAttributionTitle = hasRichSummary && richSummary.display_name;
  const hasPlaceInfo = !!placeSummary;
  return {
    hasRichSummary,
    hasRichAttributionTitle,
    hasPlaceInfo,
  };
};

export const fetchDescriptions = (pinJoin: {
  visual_descriptions?: Array<string>,
  i18n_visual_descriptions?: Array<string>,
}): Array<string> => {
  if (pinJoin.visual_descriptions && pinJoin.visual_descriptions.length > 0) {
    return pinJoin.visual_descriptions;
  } else if (pinJoin.i18n_visual_descriptions && pinJoin.i18n_visual_descriptions.length > 0) {
    return pinJoin.i18n_visual_descriptions;
  }
  return [];
};

export const processRichSummary = (pin: PinType) => {
  const { rich_summary } = pin;
  let { apple_touch_icon_images: touchIcons, favicon_images: favicons } = rich_summary;
  touchIcons = touchIcons || {};
  favicons = favicons || {};

  return {
    faviconLink: touchIcons['50x'] || favicons['50x'] || touchIcons.orig || favicons.orig,
    siteName: rich_summary.site_name || pin.domain,
    title: rich_summary.display_name,
  };
};

export const computeTitle = (pin: PinType) =>
  (pin.rich_summary && pin.rich_summary.display_name) ||
  (pin.place_summary && pin.place_summary.name) ||
  '';

export const processPlaceInfo = (pin: PinType) => {
  const { place_summary } = pin;
  return {
    faviconLink: place_summary.source_icon,
    siteName: place_summary.source_name || pin.domain,
    title: place_summary.name,
  };
};

export const getAttributionTitleText = (pin: PinType) => {
  const { hasRichAttributionTitle, hasPlaceInfo } = detectAvailable(pin);
  if (hasRichAttributionTitle) {
    return processRichSummary(pin).title;
  } else if (hasPlaceInfo) {
    return processPlaceInfo(pin).title;
  } else if (pin.rich_metadata && pin.rich_metadata.title) {
    return pin.rich_metadata.title;
  }
  return null;
};

export const getDescriptionAndAnnotationsPin = (pin: PinType) => {
  const pinJoin = pin.pin_join || {};
  let vaseAnnotations = pinJoin.visual_annotation || [];
  let vaseDescriptions = fetchDescriptions(pinJoin) || [];
  let description = pin.description || '';
  const annotationsWithLinks = vaseAnnotations.map((annotation) => {
    const annotationLinkMap = pinJoin.annotations_with_links || {};
    const annotationWithLink = annotationLinkMap[annotation] || {
      name: annotation,
      url: '',
    };
    return annotationWithLink;
  });

  const titleLower = computeTitle(pin)
    .toLocaleLowerCase()
    .trim();

  const descriptionLower = description.toLocaleLowerCase().trim();

  const firstVaseDescriptionEqualsTitle =
    vaseDescriptions.length > 0 && vaseDescriptions[0].toLocaleLowerCase().trim() === titleLower;
  const descriptionEqualsTitle = descriptionLower === titleLower;
  if (
    vaseDescriptions.length > 0 &&
    vaseDescriptions[0].length >= 3 &&
    !firstVaseDescriptionEqualsTitle
  ) {
    // Pin description quality tends to be worse than vase descriptdions, so we use the first vase description as the pin
    // description.
    if (!description || description.length < 3 || descriptionEqualsTitle) {
      // If description is too short or redundant, don't keep.
      description = vaseDescriptions[0];
      // do not shift() vaseDescriptions since that mutates props
      vaseDescriptions = (vaseDescriptions.slice(1): Array<string>);
    } else {
      // Otherwise move description to last vase description.
      vaseDescriptions = [...vaseDescriptions, description];
      // we can shift off of vaseDescriptions here b/c we set it on the previous line
      description = vaseDescriptions[0];
    }
  } else if (!description || description.length < 3 || descriptionEqualsTitle) {
    // Pin description is too short or redundant and we don't have vase descriptions to use instead.
    if (vaseAnnotations.length) {
      description = vaseAnnotations.join(', ');
      vaseAnnotations = [];
    } else {
      description = '';
    }
  }

  if (description) {
    description = removeMultipleSpaces(smartTruncate(description, descriptionMaxLength)).trim();
  }

  return { description, vaseAnnotations, vaseDescriptions, annotationsWithLinks };
};
